import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard/Dashboard';
import Clientes from './pages/Clientes/Clientes';
import ClienteForm from './pages/Clientes/ClienteForm';
import ClienteDetalle from './pages/Clientes/ClienteDetalle';
import ClienteCreditos from './pages/Clientes/ClienteCreditos';
import ClienteHistorial from './pages/Clientes/ClienteHistorial';
import ClienteEstadoCuenta from './pages/Clientes/ClienteEstadoCuenta';
import ClientePagos from './pages/Clientes/ClientePagos';
import ClientePagar from './pages/Clientes/ClientePagar';
import ClienteNotas from './pages/Clientes/ClienteNotas';
import ClienteMensajes from './pages/Clientes/ClienteMensajes';
import Productos from './pages/Productos/Productos';
import ProductoForm from './pages/Productos/ProductoForm';
import ProductoDetalle from './pages/Productos/ProductoDetalle';
import ProductoInventario from './pages/Productos/ProductoInventario';
import ProductoVentas from './pages/Productos/ProductoVentas';
import Formulaciones from './pages/Formulaciones/Formulaciones';
import FormulacionForm from './pages/Formulaciones/FormulacionForm';
import FormulacionDetalle from './pages/Formulaciones/FormulacionDetalle';
import { ProcesosMenu, ProcesosList, Procesos, ProcesoForm } from './pages/Procesos';
import Inventario from './pages/Inventario/Inventario';
import AjusteStockForm from './pages/Inventario/AjusteStockForm';
import Proveedores from './pages/Proveedores/Proveedores';
import ProveedorForm from './pages/Proveedores/ProveedorForm';
import ProveedorDetalle from './pages/Proveedores/ProveedorDetalle';
import ProveedorOrdenes from './pages/Proveedores/ProveedorOrdenes';
import ProveedorNotas from './pages/Proveedores/ProveedorNotas';
import Vendedores from './pages/Vendedores/Vendedores';
import VendedorForm from './pages/Vendedores/VendedorForm';
import VendedorDetalle from './pages/Vendedores/VendedorDetalle';
import VendedorVentas from './pages/Vendedores/VendedorVentas';
import VendedorComisiones from './pages/Vendedores/VendedorComisiones';
import VendedorClientes from './pages/Vendedores/VendedorClientes';
import VendedorGrafica from './pages/Vendedores/VendedorGrafica';
import VendedorNotas from './pages/Vendedores/VendedorNotas';
import Ventas from './pages/Ventas/Ventas';
import VentaDetalle from './pages/Ventas/VentaDetalle';
import OrdenForm from './pages/Ventas/OrdenForm';
import PreOrderForm from './pages/Ordenes/PreOrderForm';
import { Ordenes, OrdenFormulario, OrdenDetalle } from './pages/Ordenes';
import { Facturacion, FacturaForm, FacturaDetalle } from './pages/Facturacion';
import { Compras, CompraForm, CompraDetalle, CompraRecibir } from './pages/Compras';
import {
  Reportes,
  ReportesMenu,
  ReportesVentas,
  ReportesInventario,
  ReportesImpuestos,
  ReportesRetornos,
  ReportesCreditos
} from './pages/Reportes';
import Prospeccion from './pages/Prospeccion';
import ProspectoForm from './pages/Prospeccion/ProspectoForm';
import {
  Admin,
  // AdminDashboard,
  Users,
  Roles,
  RoleFormPage,
  Permissions,
  InvoicingSettings,
  PurchaseOrderSettings,
  EmailSettings,
  SystemSettings
} from './pages/Admin';
import { PortalHome, EstadoCuenta, CrearOrden, HistorialCompras, NuevosProductos, Ofertas, PagarFactura } from './pages/Portal';
import Login from './pages/Auth/Login';
import ForcePasswordChange from './pages/Auth/ForcePasswordChange';
import EliminacionDatos from './pages/Legal/EliminacionDatos';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { NotificationProvider } from './components/UI/Notifications/NotificationProvider';
import TokenStatus from './components/Debug/TokenStatus';
import { debugToken } from './utils/debugToken';
import './styles/main.scss';

// Hacer debugToken disponible globalmente en desarrollo
if (process.env.NODE_ENV === 'development') {
  window.debugToken = false;
}

// Componente para rutas protegidas
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#7b2c7f'
      }}>
        Cargando...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si el usuario debe cambiar su contraseña, forzar la pantalla de cambio
  if (user?.must_change_password) {
    return <Navigate to="/cambiar-password" replace />;
  }

  // Si es una ruta solo para admin y el usuario es CLIENT, redirigir al portal
  if (adminOnly && (user?.role || '').toLowerCase() === 'client') {
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// Componente para rutas de administración (requiere permisos)
const AdminRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, user, hasPermission, loading } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#7b2c7f'
      }}>
        Cargando...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si el usuario debe cambiar su contraseña, forzar la pantalla de cambio
  if (user?.must_change_password) {
    return <Navigate to="/cambiar-password" replace />;
  }

  // Si es CLIENT, redirigir al portal
  if ((user?.role || '').toLowerCase() === 'client') {
    return <Navigate to="/portal" replace />;
  }

  // Si requiere permiso específico y no lo tiene, redirigir al dashboard
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/system" replace />;
  }

  return children;
};

// Componente para rutas públicas (solo para usuarios no autenticados)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#7b2c7f'
      }}>
        Cargando...
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirigir según el rol
    if ((user?.role || '').toLowerCase() === 'client') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/system" replace />;
  }

  return children;
};

// Componente para la ruta raíz "/" que redirige según el estado
const HomeRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#7b2c7f'
      }}>
        Cargando...
      </div>
    );
  }

  if (!isAuthenticated) {
    // Usuario no autenticado → Mostrar portal público
    return <Navigate to="/portal" replace />;
  }

  if ((user?.role || '').toLowerCase() === 'client') {
    // Cliente autenticado → Portal del cliente
    return <Navigate to="/portal" replace />;
  }

  // Usuario STAFF → Dashboard ERP
  return <Navigate to="/system" replace />;
};

// Ruta del cambio de contraseña obligatorio: requiere auth pero NO fuerza el
// redirect (evita un bucle con la propia pantalla de cambio).
const PasswordChangeRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#7b2c7f'
      }}>
        Cargando...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
      <CartProvider>
        <Router>
          <div className="App">
          <Routes>
            {/* Ruta raíz - Redirige según estado de autenticación */}
            <Route path="/" element={<HomeRedirect />} />

            {/* Rutas públicas */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />

            {/* Cambio de contraseña obligatorio (must_change_password) */}
            <Route path="/cambiar-password" element={
              <PasswordChangeRoute>
                <ForcePasswordChange />
              </PasswordChangeRoute>
            } />

            {/* Eliminación de datos de usuario - Público (requisito de Google) */}
            <Route path="/eliminacion-de-datos" element={<EliminacionDatos />} />
            <Route path="/data-deletion" element={<EliminacionDatos />} />

            {/* Portal del Cliente - Público (muestra info, pide login para acciones) */}
            <Route path="/portal" element={<PortalHome />} />

            {/* Rutas del Portal que requieren autenticación CLIENT */}
            <Route path="/portal/historial-productos" element={
              <ProtectedRoute>
                <EstadoCuenta />
              </ProtectedRoute>
            } />
            <Route path="/portal/crear-orden" element={
              <ProtectedRoute>
                <CrearOrden />
              </ProtectedRoute>
            } />
            <Route path="/portal/pagar-factura" element={
              <ProtectedRoute>
                <PagarFactura />
              </ProtectedRoute>
            } />
            <Route path="/portal/historial" element={
              <ProtectedRoute>
                <HistorialCompras />
              </ProtectedRoute>
            } />
            <Route path="/portal/nuevos-productos" element={
              <ProtectedRoute>
                <NuevosProductos />
              </ProtectedRoute>
            } />
            <Route path="/portal/ofertas" element={
              <ProtectedRoute>
                <Ofertas />
              </ProtectedRoute>
            } />

            {/* Rutas protegidas del ERP Administrativo */}
            <Route path="/system" element={
              <ProtectedRoute adminOnly={true}>
                <Layout />
              </ProtectedRoute>
            }>
              {/* Dashboard */}
              <Route index element={<Dashboard />} />
              
              {/* Clientes */}
              <Route path="clientes" element={<Clientes />} />
              <Route path="clientes/nuevo" element={<ClienteForm />} />
              <Route path="clientes/:id" element={<ClienteDetalle />} />
              <Route path="clientes/:id/editar" element={<ClienteForm />} />
              <Route path="clientes/:id/creditos" element={<ClienteCreditos />} />
              <Route path="clientes/:id/historial" element={<ClienteHistorial />} />
              <Route path="clientes/:id/estado-cuenta" element={<ClienteEstadoCuenta />} />
              <Route path="clientes/:id/pagos" element={<ClientePagos />} />
              <Route path="clientes/:id/pagar" element={<ClientePagar />} />
              <Route path="clientes/:id/notas" element={<ClienteNotas />} />
              <Route path="clientes/:id/mensajes" element={<ClienteMensajes />} />
              
              {/* Productos */}
              <Route path="productos" element={<Productos />} />
              <Route path="productos/nuevo" element={<ProductoForm />} />
              <Route path="productos/:id" element={<ProductoDetalle />} />
              <Route path="productos/:id/editar" element={<ProductoForm />} />
              <Route path="productos/:id/ventas" element={<ProductoVentas />} />
              <Route path="productos/:id/inventario" element={<ProductoInventario />} />
              <Route path="productos/:id/formulacion" element={<ProductoDetalle />} />

              {/* Formulaciones */}
              <Route path="formulaciones" element={<Formulaciones />} />
              <Route path="formulaciones/:id" element={<FormulacionDetalle />} />

              {/* Inventario */}
              <Route path="inventario" element={<Inventario />} />
              <Route path="inventario/ajustar" element={<AjusteStockForm />} />

              {/* Proveedores */}
              <Route path="proveedores" element={<Proveedores />} />
              <Route path="proveedores/nuevo" element={<ProveedorForm />} />
              <Route path="proveedores/:id/editar" element={<ProveedorForm />} />
              <Route path="proveedores/:id/ordenes" element={<ProveedorOrdenes />} />
              <Route path="proveedores/:id/notas" element={<ProveedorNotas />} />
              <Route path="proveedores/:id" element={<ProveedorDetalle />} />
              
              {/* Vendedores */}
              <Route path="vendedores" element={<Vendedores />} />
              <Route path="vendedores/nuevo" element={<VendedorForm />} />
              <Route path="vendedores/:id" element={<VendedorDetalle />} />
              <Route path="vendedores/:id/editar" element={<VendedorForm />} />
              <Route path="vendedores/:id/ventas" element={<VendedorVentas />} />
              <Route path="vendedores/:id/comisiones" element={<VendedorComisiones />} />
              <Route path="vendedores/:id/clientes" element={<VendedorClientes />} />
              <Route path="vendedores/:id/grafica" element={<VendedorGrafica />} />
              <Route path="vendedores/:id/notas" element={<VendedorNotas />} />
              
              {/* Ventas y Estimados */}
              <Route path="ventas" element={<Ventas />} />
              <Route path="ventas/nueva-orden" element={<OrdenForm />} />
              <Route path="ventas/nueva-preorden" element={<PreOrderForm />} />
              <Route path="ventas/nueva" element={<OrdenForm />} />
              <Route path="ventas/:id/editar" element={<OrdenForm />} />
              <Route path="ventas/:id" element={<VentaDetalle />} />

              {/* Órdenes - REDIRECCION A VENTAS (legacy) */}
              <Route path="ordenes" element={<Navigate to="/system/ventas" replace />} />
              <Route path="ordenes/*" element={<Navigate to="/system/ventas" replace />} />

              {/* Compras */}
              <Route path="compras" element={<Compras />} />
              <Route path="compras/nueva" element={<CompraForm />} />
              <Route path="compras/:id" element={<CompraDetalle />} />
              <Route path="compras/:id/editar" element={<CompraForm />} />
              <Route path="compras/:id/recibir" element={<CompraRecibir />} />

              {/* Procesos de Producción - Nueva UI */}
              <Route path="procesos" element={<ProcesosMenu />} />
              <Route path="procesos/encapsulado" element={<ProcesosList />} />
              <Route path="procesos/tes" element={<ProcesosList />} />
              <Route path="procesos/empacado" element={<ProcesosList />} />
              <Route path="procesos/limpieza" element={<ProcesosList />} />
              <Route path="procesos/recibir" element={<ProcesosList />} />
              <Route path="procesos/retornos" element={<ProcesosList />} />
              <Route path="procesos/mantenimiento" element={<ProcesosList />} />

              {/* Procesos - Vista Legacy (opcional) */}
              <Route path="procesos/legacy" element={<Procesos />} />
              <Route path="procesos/nuevo" element={<ProcesoForm />} />
              <Route path="procesos/:id" element={<div>Detalle Proceso - Por implementar</div>} />
              <Route path="procesos/:id/editar" element={<ProcesoForm />} />

              {/* Facturación */}
              <Route path="facturacion" element={<Facturacion />} />
              <Route path="facturacion/pagadas" element={<Facturacion />} />
              <Route path="facturacion/pendientes" element={<Facturacion />} />
              <Route path="facturacion/vencidas" element={<Facturacion />} />
              <Route path="facturacion/reporte-ventas" element={<Facturacion />} />
              <Route path="facturacion/nueva" element={<FacturaForm />} />
              <Route path="facturacion/:id/editar" element={<FacturaForm />} />
              <Route path="facturacion/:id" element={<FacturaDetalle />} />

              {/* Reportes */}
              <Route path="reportes" element={<ReportesMenu />} />
              <Route path="reportes/ventas" element={<ReportesVentas />} />
              <Route path="reportes/inventario" element={<ReportesInventario />} />
              <Route path="reportes/impuestos" element={<ReportesImpuestos />} />
              <Route path="reportes/retornos" element={<ReportesRetornos />} />
              <Route path="reportes/creditos" element={<ReportesCreditos />} />
              <Route path="reportes/legacy" element={<Reportes />} />

              {/* Prospección - Seguimiento de Nuevos Clientes */}
              <Route path="seguimiento" element={<Prospeccion />} />
              <Route path="seguimiento/nuevo" element={<ProspectoForm />} />
              <Route path="seguimiento/editar/:id" element={<ProspectoForm />} />
              <Route path="prospeccion" element={<Prospeccion />} />

              {/* DEPRECATED - Redirect antiguo de registros a procesos */}
              <Route path="registros" element={<Navigate to="/system/procesos" replace />} />
              
              {/* Administración - Rutas anidadas con tabs */}
              <Route path="admin" element={
                <AdminRoute requiredPermission="admin.users">
                  <Admin />
                </AdminRoute>
              }>
                <Route index element={<Navigate to="/system/admin/usuarios" replace />} />
                <Route path="usuarios" element={<Users />} />
                <Route path="roles" element={<Roles />} />
                <Route path="roles/nuevo" element={<RoleFormPage />} />
                <Route path="roles/:id/editar" element={<RoleFormPage />} />
                <Route path="permisos" element={<Permissions />} />

                {/* Configuraciones del sistema */}
                <Route path="configuracion" element={<Navigate to="/system/admin/configuracion/facturacion" replace />} />
                <Route path="configuracion/facturacion" element={<InvoicingSettings />} />
                <Route path="configuracion/ordenes" element={<PurchaseOrderSettings />} />
                <Route path="configuracion/email" element={<EmailSettings />} />
                <Route path="configuracion/sistema" element={<SystemSettings />} />
              </Route>
            </Route>

            {/* Ruta por defecto - Redirige al portal */}
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
          </div>
        </Router>
      </CartProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
